// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import { withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import Loading from 'react-loading';

import Modal from '../Modal';
import Checkbox from '../../Checkbox';
import api from '../../../api';

import uploadFormats from '../../../data/formats';
import formatNumber from '../../../utilities/formatNumber';
import formatCurrency from '../../../utilities/formatCurrency';
import calculateBulkCost from '../../../utilities/calculateBulkCost';

import { DROPBOX_CLIENT_ID } from '../../../data/keys';
import '../../../styles/import.css';

class ImportDropbox extends Modal {

    state = {
        oauth: null,
        loading: true,
        alert: false,
        folders: [], // Dropbox folders to sync
        expanded: [],
        stacks: [],
        avgNPages: 5,
        moveUploadedFiles: false
    }

    componentDidMount = async () => {
        const res = await api.get('/stack/all?minimal=true');
        if (res && res.success && res.stacks)
            this.setState({ stacks: res.stacks });
        else return toast.error("Failed to load stacks list");

        this.waitForTeamLoad = setInterval(() => {
            if (!this.props.team || !this.props.team.sync)
                return; // Try again...

            // Clear interval
            clearInterval(this.waitForTeamLoad);
            this.authenticateAndLoadRoot();
        }, 1000);
    }
    
    componentWillUnmount = ()=> {
        // Get the url params. 
        const params = new URLSearchParams(window.location.search); 
        // Do we have a state? 
        let state = params.get('state'); 
        if(!state)
            return;

        state = JSON.parse(state);
        const redirect = state.redirect??null;
        if(redirect)
            window.close(); 
    }

    authenticateAndLoadRoot = async () => {
        await this.setState({ loading: true });
        await this.getOAuth2Token();
        setTimeout(async () => {
            const folders = await this.listDropboxFolders();
            this.setState({
                folders,
                loading: false
            });
        },0);
    }

    getOAuth2Token = async () => {
        // Do we already have a token?
        const { code, connectionIdx } = this.props.data;
        if (!code && connectionIdx >= 0) {
            const connection = this.props.team.sync.dropbox[connectionIdx];
            const res = await api.get("/sync/dropbox/refresh?oauth=" + connection.oauth);
            if (res && res.success) {
                this.setState({ moveUploadedFiles: connection?.moveUploadedFiles});
                return this.setState({ oauth: connection.oauth });
            }
            // Re-authenticate...
            window.location.href = "https://www.dropbox.com/oauth2/authorize?client_id=" + DROPBOX_CLIENT_ID + "&response_type=code&token_access_type=offline&redirect_uri=" + encodeURIComponent(api.publicUrl + "/settings/import/dropbox");
            return;
        }

        // Code provided, or not reconnecting... Generate tokens with code
        const res = await api.post("/sync/dropbox/token", { code });
        if (!res || !res.oauthId)
            return toast.error("Unable to get a secure token from the Dropbox API");

        await this.setState({ oauth: res.oauthId });

        // Do we have a state? 
        const params = new URLSearchParams(window.location.search); 

        let state = params.get('state'); 

        if (state) {
            // Enable Dropbox Import
            const connections = this.props.team && this.props.team.sync && this.props.team.sync.dropbox && this.props.team.sync.dropbox.length ? this.props.team.sync.dropbox : [];

            let connection;
            if (connectionIdx >= 0 && connections.length > connectionIdx)
                connection = connections[connectionIdx];
            else {
                // Connection doesn't exist... create one
                connection = {
                    creationDate: new Date(),
                    user: this.props.user._id
                }
            }
                
            // Common updates
            connection.enabled = true;
            connection.folders = [];
            connection.oauth = res.oauthId;
            connection.bulk = false;
            connection.lastModified = new Date();
            connection.error = "";
            connection.moveUploadedFiles = this.state.moveUploadedFiles;

            if (connectionIdx >= 0 && connections.length > connectionIdx)
                connections[connectionIdx] = connection;
            else 
                connections.push(connection); // Create new

            const sync = this.props.team ? this.props.team.sync : {};
            sync.dropbox = connections;
            await this.props.updateTeam({ sync });

            this.props.modal();
        }
    }

    getSyncedFolders = () => {
        const team = this.props.team;
        const { connectionIdx } = this.props.data;
        return connectionIdx >= 0 && team && team.sync && team.sync.dropbox && team.sync.dropbox && team.sync.dropbox.length > connectionIdx ? team.sync.dropbox[connectionIdx].folders : [];
    }

    isSynced = (synced, entry) => {
        if (!synced || !synced.length)
            return false;

        // Already synced? (It is if a parent folder is synced)
        let sync = synced.map(f => f.id).includes(entry.id);
        if (sync) return true;

        for (const f of synced) {
            if (entry.path_lower.substring(0, f.source.length) === f.source) {
                // f is a parent of entry, and f is synced - so this is automatically synced...
                return true;
            }
        }

        return false;
    }

    getChildrenRecursive = async (folderId, folderPath="") => {
        let cursor;
        let children = [];
        let nDocs = 0;
        const synced = this.getSyncedFolders();

        while (true) {
            const res = await api.post("/sync/dropbox/list", {
                oauth: this.state.oauth,
                cursor,
                folderPath,
                folderId,
                recursive: true
            });
            if (!res || !res.entries)
                break;

            // Setup children folders first
            for (const entry of res.entries) {
                if (entry[".tag"] !== "folder" || folderPath === entry.path_lower)
                    continue;

                children.push({
                    name: entry.name,
                    path: entry.path_lower,
                    path_display: entry.path_display,
                    shared: false,
                    id: entry.id,
                    sync: this.isSynced(synced, entry),
                    nDocs: 0
                });
            }

            // Now count documents in each of hte sub-folders
            for (const entry of res.entries) {
                if (entry[".tag"] !== "file" || !uploadFormats.includes(entry.name.split(".").pop().toLowerCase())) // Supported file formats only
                    continue
                
                // Count nDocs in all parent folders up to root
                for (const f of children) {
                    if (entry.path_lower.substring(0, f.path.length) === f.path)
                        f.nDocs++;
                }

                // Root count
                nDocs++;
            }
            
            if (res.has_more && res.cursor)
                cursor = res.cursor;
            else break;
        }

        const updateResult = async () => {
            this.setState(prevState => {
                const folders = this.state.folders.slice();
                const fIdx = folders.findIndex(f => f.id === folderId);
                if (fIdx < 0) {
                    setTimeout(updateResult, 250);
                    return prevState;
                }
                folders[fIdx].nDocs = nDocs;
                return {
                    ...prevState,
                    folders: [ ...folders, ...children ],
                };
            });
        };
        updateResult();
    }

    listDropboxFolders = async () => {
        const synced = this.getSyncedFolders();

        let pFolders = [];
        // Personal folders
        let cursor;
        while (true) {
            const res = await api.post("/sync/dropbox/list", {
                oauth: this.state.oauth,
                cursor,
                folderPath: ""
            });
            if (!res || !res.entries)
                break;
            for (const entry of res.entries)
                if (entry[".tag"] === "folder") {
                    pFolders.push({
                        name: entry.name,
                        path: entry.path_lower,
                        path_display: entry.path_display,
                        shared: false,
                        id: entry.id,
                        sync: this.isSynced(synced, entry),
                        nDocs: -1
                    });
                    this.getChildrenRecursive(entry.id, entry.path_lower);
                }
            if (res.has_more && res.cursor)
                cursor = res.cursor;
            else break;
        }

        // Shared folders
        let sFolders = [];
        // cursor = undefined;
        // while (true) {
        //     const res = await dropboxApiRequest(
        //         "https://api.dropboxapi.com/2/sharing/list_folders" + (cursor ? "/continue" : "") + "?authorization=Bearer " + this.state.token,
        //         { cursor }
        //     );
        //     if (!res || !res.entries)
        //         break;
        //     for (const entry of res.entries)
        //         if (entry.parent_folder_name === "") {
        //             sFolders.push({
        //                 name: entry.name,
        //                 path: "/" + entry.name.toLowerCase(),
        //                 shared: true,
        //                 id: entry.shared_folder_id,
        //                 url: entry.preview_url
        //             });
        //             this.getChildrenRecursive(entry.shared_folder_id);
        //         }
        //     if (res.has_more && res.cursor)
        //         cursor = res.cursor;
        //     else break;
        // }

        return [
            ...(pFolders.sort((a,b) => a.name > b.name ? 1 : -1)),
            ...(sFolders.sort((a,b) => a.name > b.name ? 1 : -1))
        ];
    }

    atleastOneFolderSelected = () => {
        for (const f of this.state.folders)
            if (f.sync) return true;
        return false;
    }

    getBulkImportFileCount = () => {
        let nDocs = 0;
        for (const f of this.state.folders)
            if (f.sync && f.nDocs > 0)
                nDocs += f.nDocs;
        return nDocs;
    }

    renderFolders = (parent, indent=0) => {
        let folders = [];
        for (const f of this.state.folders) {
            // Not the parent directory!
            if (f.path === parent)
                continue;

            // Only folders inside the given parent directory
            if (f.path.substring(0, parent.length) !== parent)
                continue;

            // Only one level down...
            const subpath = f.path.substring(parent.length+1);
            const cmp = subpath.split("/");

            if (cmp.length > 1)
                continue;

            folders.push(f); // We can display this folder...
        }

        return (<>
            {folders.map((f,idx) => {
                // Are there any folders inside?
                const nested = this.state.folders.filter(g => g.id !== f.id && g.path.substring(0, f.path.length) === f.path).length > 0;

                return <div key={idx}>
                    <div
                        className="folder"
                        style={{
                            paddingLeft: String(8 + (indent*32)) + "px"
                        }}
                        title={f.name}
                    >

                        {/* Dropdown arrow */}
                        {nested ? <div
                            className="config-stack-dropdown-arrow folder-select-dropdown-control"
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                this.setState({
                                    expanded: {
                                        ...this.state.expanded,
                                        [f.id]: this.state.expanded[f.id] ? false : true
                                    }
                                })
                            }}
                        >
                            {this.state.expanded[f.id] ? "▾" : "▸"}
                        </div> : <div className="config-stack-no-dropdown folder-select-dropdown-control">▸</div>}

                        <img
                            src={"https://static.stax.ai/images/icons/folder" + (f.shared ? "-shared" : "") + ".png"}
                            alt=""
                        />
                        <div className="folder-name">{f.name}</div>
                        {f.nDocs >= 0 ? <div className="folder-ndocs">({formatNumber(f.nDocs)} files)</div> : <div className="folder-loading"><Loading
                            type="spin"
                            color="var(--color-accent)"
                            height={20}
                            width={20}
                        /></div>}
                        {f.nDocs >= 0 && <Checkbox
                            className="import-checkbox"
                            tooltip="Import and sync folder to Stax·ai. Right-click for options."
                            checked={f.sync}
                            onToggle={() => {
                                const sync = !f.sync;
                                let folders = [ ...this.state.folders ];

                                // Nested operations...
                                for (const g of folders) {
                                    // Apply to this and all sub-folders inside this one
                                    if (g.path.substring(0, f.path.length) === f.path)
                                        g.sync = sync;

                                    // If we're unchecking a folder sync, then make sure we also uncheck all parents up to root
                                    if (!sync && f.path.substring(0, g.path.length) === g.path)
                                        g.sync = false;
                                }
                                
                                this.setState({ folders });
                            }}
                        />}
                    </div>

                    {/* Nested folders here... */}
                    {nested && this.state.expanded[f.id] ? this.renderFolders(f.path, indent+1) : ""}
                </div>;
            })}
        </>);
    }

    renderContent = () => {
        const nDocs = this.getBulkImportFileCount();
        return (<div className="modal-content modal-center">

            <h1>Sync documents from Dropbox</h1>

            <p>
                Stax·ai will make stacks for each Dropbox folder you select below and mirror the contents of these folders into your new stacks.
            </p><br />
            
            <h2>Select Dropbox folders to import</h2>
            <div style={{ display: 'inline-table'}}>
                <div className="folder-select">
                    {this.state.loading ? <div style={{
                        marginLeft: 300,
                        marginTop: 60
                    }}><Loading
                        type="bubbles"
                        color="var(--color-primary)"
                        height={100}
                        width={100}
                    /></div> : this.state.folders && this.state.folders.length > 0 ? this.renderFolders("/") : <div style={{ textAlign: 'center' }}>We couldn't find any folders...</div>}
                </div>
                {this.atleastOneFolderSelected(this.state.folders) && <div className="import-checkbox-label"> Move uploaded files to the 'Upload Complete' folder after import
                    <Checkbox
                        className="import-checkbox"
                        tooltip="Move uploaded files to the 'Upload Complete' folder after import"
                        checked={this.state.moveUploadedFiles}
                        onToggle={() => this.setState({ moveUploadedFiles: !this.state.moveUploadedFiles })}
                    />
            </div>}
        </div>
            {this.atleastOneFolderSelected(this.state.folders) && <div className="import-control">
                <div
                    className="button"
                    style={{
                        float: 'none',
                        backgroundImage: "url('https://static.stax.ai/images/icons/import.png')"
                    }}
                    onClick={async () => {
                        const { connectionIdx } = this.props.data;

                        await this.props.loading(2000, "Setting up...");

                        // Compile folders to import
                        const synced = this.state.folders.filter(f => f.sync);

                        let folders = [];
                        for (const f of synced) {
                            // If a parent directory is being synced, then ignore
                            let parentSynced = false;
                            for (const g of synced) {
                                if (f.id === g.id) continue; // Don't check against itself
                                if (f.path.substring(0,g.path.length) === g.path) {
                                    // g is a parent of f. Don't sync f.
                                    parentSynced = true;
                                    break;
                                }
                            }

                            if (parentSynced) continue; // Skip

                            // If the folder exists in Stax·ai, use that. Else, create folder in Stax·ai
                            let destination;
                            // let path = f.path_display;
                            // if (path[0] === '/')
                            //     path = path.substring(1);
                            let path = f.name;

                            const stack = this.state.stacks.find(s => s.path === path);
                            if (stack)
                                destination = stack._id;

                            // Stack doesn't exist - create it
                            else {
                                const res = await api.post('/stack/create', { stackPath: path });
                                if (!res || !res.stack) {
                                    toast.error("Failed to create stack: " + path);
                                    return this.props.loaded();
                                }
                                destination = res.stack._id;
                            }

                            // We're good - sync this folder...
                            folders.push({
                                source: f.path,
                                name: f.name,
                                id: f.id,
                                shared: f.shared,
                                destination
                            });
                        }
                        
                        // Enable Dropbox Import
                        const connections = this.props.team && this.props.team.sync && this.props.team.sync.dropbox && this.props.team.sync.dropbox.length ? this.props.team.sync.dropbox : [];

                        let connection;
                        if (connectionIdx >= 0 && connections.length > connectionIdx)
                            connection = connections[connectionIdx];
                        else {
                            // Connection doesn't exist... create one
                            connection = {
                                creationDate: new Date(),
                                user: this.props.user._id
                            }
                        }
                            
                        // Common updates
                        connection.enabled = true;
                        connection.folders = folders;
                        connection.oauth = this.state.oauth;
                        connection.bulk = true;
                        connection.lastModified = new Date();
                        connection.error = "";
                        connection.moveUploadedFiles = this.state.moveUploadedFiles;

                        if (connectionIdx >= 0 && connections.length > connectionIdx)
                            connections[connectionIdx] = connection;
                        else 
                            connections.push(connection); // Create new

                        const sync = this.props.team ? this.props.team.sync : {};
                        sync.dropbox = connections;
                        await this.props.updateTeam({ sync });

                        // Force a manual sync now
                        const res = await api.get('/sync/connections?source=dropbox');
                        if (res && res.connections && res.connections.length)
                            await api.post('/sync/manual', {
                                connectionId: res.connections[connectionIdx >= 0 ? connectionIdx : connections.length-1]._id,
                                source: "dropbox"
                            });

                        toast.info("Your Dropbox files will start syncing shortly");
                        this.props.loaded();
                        this.props.modal();
                    }}
                >Import Selected Folders</div>
                <div className="import-cost">
                    You are requesting a <strong>bulk import</strong> of {formatNumber(nDocs)} documents.<br />
                    With an estimated average of <input
                        className="input-inline"
                        type="text"
                        value={this.state.avgNPages}
                        onChange={e => {
                            const n = parseInt(e.target.value);
                            this.setState({ avgNPages: isNaN(n) ? 0 : n });
                        }}
                    /> pages per document,<br />
                    your bulk import charge will be approx. <strong>{formatCurrency(calculateBulkCost(this.state.avgNPages*nDocs))}.</strong>
                </div>
            </div>}
            
        </div>);
    }

};

export default withRouter(ImportDropbox);